import React, { useState } from 'react';
import { QuestionBlock } from '../types';
import BEM from 'common/bem';

const classes = BEM.with('QuestionPrompt');

type QuestionPromptProps = {
  data: QuestionBlock;
  onSelect: (value: string, label?: string) => void;
  disabled?: boolean;
};

export default function QuestionPrompt({ data, onSelect, disabled }: QuestionPromptProps) {
  const isMulti =
    data.questionType === 'multi_choice' || data.questionType === 'multi_choice_ordered';
  const isOrdered = data.questionType === 'multi_choice_ordered';
  const hasOptions = data.questionType !== 'free_text' && data.options && data.options.length > 0;

  // selectedValues holds values in click order — order matters for multi_choice_ordered
  const [selectedValues, setSelectedValues] = useState<string[]>([]);

  const toggleOption = (value: string) => {
    setSelectedValues((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const handleConfirm = () => {
    if (selectedValues.length === 0) return;
    const options = data.options ?? [];
    const labels = selectedValues.map((v) => options.find((o) => o.value === v)?.label ?? v);
    onSelect(labels.join(', '));
  };

  return (
    <div className={classes()}>
      <p className={classes('prompt')}>
        <svg
          className={classes('prompt-icon')}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        {data.prompt}
      </p>

      {isMulti && selectedValues.length > 0 && (
        <div className={classes('selected-chips')}>
          {selectedValues.map((v, idx) => {
            const opt = (data.options ?? []).find((o) => o.value === v);
            return (
              <span key={v} className={classes('chip')}>
                {isOrdered && <span className={classes('chip-order')}>{idx + 1}</span>}
                {opt?.label ?? v}
                <button
                  className={classes('chip-remove')}
                  onClick={() => toggleOption(v)}
                  disabled={disabled}
                  type="button"
                  aria-label={`Remove ${opt?.label ?? v}`}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}

      {hasOptions && (
        <div className={classes('options')}>
          {(data.options ?? []).map((option) => {
            if (isMulti) {
              const isSelected = selectedValues.includes(option.value);
              return (
                <button
                  key={option.value}
                  className={classes('option-btn', { selected: isSelected })}
                  onClick={() => toggleOption(option.value)}
                  disabled={disabled}
                  type="button"
                  aria-pressed={isSelected}
                >
                  {option.label}
                </button>
              );
            }
            return (
              <button
                key={option.value}
                className={classes('option-btn')}
                onClick={() => onSelect(option.value, option.label)}
                disabled={disabled}
                type="button"
              >
                {option.label}
              </button>
            );
          })}
        </div>
      )}

      {isMulti && hasOptions && (
        <button
          className={classes('confirm-btn')}
          onClick={handleConfirm}
          disabled={disabled || selectedValues.length === 0}
          type="button"
        >
          {`Confirm selection${selectedValues.length > 0 ? ` (${selectedValues.length})` : ''}`}
        </button>
      )}
    </div>
  );
}
